import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout'; // Reuse tokens
import { BreadcrumbJsonLd } from '../../components/StructuredData';

const solutions = [
    {
        category: "Platform Outcomes",
        items: [
            {
                path: "/continuous-audit-evidence",
                title: "Continuous Audit Evidence",
                desc: "Tie approvals, branch protection changes, and exceptions into a continuous audit timeline."
            },
            {
                path: "/github-change-control",
                title: "Change Control Evidence",
                desc: "Real-time records of changes, approvals, and reviews to support audit evidence collection."
            },
            {
                path: "/engineering-audit-toil",
                title: "Engineering Audit Toil",
                desc: "Reduce manual evidence collection by exporting tamper-evident packs from GitHub metadata."
            },
            {
                path: "/auditor-self-serve",
                title: "Auditor Self-Serve",
                desc: "Use guest mode to provide 24-hour, read-only evidence exports without account access."
            }
        ]
    },
    {
        category: "Compliance Frameworks",
        items: [
            {
                path: "/soc2-change-management-evidence",
                title: "SOC 2 Change Management",
                desc: "Automated evidence for CC8.1 (Change Management) and CC6.1 (Logical Access) — continuous Type II capture."
            },
            {
                path: "/soc2-github-compliance",
                title: "SOC 2 GitHub Guide",
                desc: "Complete GitHub configuration checklist and continuous evidence guide for SOC 2 Type II audits."
            },
            {
                path: "/iso-27001-github-compliance",
                title: "ISO 27001 GitHub Guide",
                desc: "GitHub configuration, A.8.32 control mapping, and continuous evidence for annual surveillance audits."
            },
            {
                path: "/solutions/iso-27001-a832-evidence",
                title: "ISO 27001 A.8.32 Deep Dive",
                desc: "Control-level mapping of GitHub PR approvals, branch protection, and CI results to A.8.32."
            },
            {
                path: "/iso-27001-vs-soc2",
                title: "ISO 27001 vs SOC 2",
                desc: "Compare frameworks, identify the 60–70% evidence overlap, and pursue both efficiently."
            },
            {
                path: "/eu-ai-act-github-compliance",
                title: "EU AI Act GitHub Guide",
                desc: "Article 9 QMS and Article 14 Copilot oversight evidence — GitHub configuration for August 2026."
            },
            {
                path: "/audit-readiness",
                title: "Audit Readiness",
                desc: "Stay audit-ready for SOC 2 and ISO 27001 with continuous evidence collection."
            }
        ]
    },
    {
        category: "Use Cases",
        items: [
            {
                path: "/github-copilot-compliance",
                title: "GitHub Copilot Compliance",
                desc: "Detect AI co-author signals and link them to human reviewer approvals for EU AI Act Article 14."
            },
            {
                path: "/ai-assisted-development-evidence",
                title: "AI-Assisted Development",
                desc: "Evidence that human review occurred for code generated by Copilot and other AI tools."
            },
            {
                path: "/github-change-control",
                title: "GitHub Change Control",
                desc: "Turn GitHub pull requests and reviews into formal change records automatically."
            },
            {
                path: "/outsourced-software-change-control",
                title: "Outsourced Development",
                desc: "Independent verification of controls when working with external agencies or contractors."
            }
        ]
    },
    {
        category: "Technical Evidence",
        items: [
            {
                path: "/read-only-github-audit",
                title: "Read-Only GitHub Audit",
                desc: "Collect GitHub audit evidence securely without write access to your repositories."
            },
            {
                path: "/branch-protection-drift-detection",
                title: "Branch Protection Drift Detection",
                desc: "Track changes to branch protection rules and detect when controls are weakened."
            },
            {
                path: "/github-workflow-drift-detection",
                title: "Workflow Drift Detection",
                desc: "Monitor changes to GitHub Actions workflows and required status checks."
            },
            {
                path: "/secure-evidence-sharing",
                title: "Secure Evidence Sharing",
                desc: "Share tamper-evident evidence packs with auditors using time-limited, format-controlled links."
            },
            {
                path: "/ci-evidence",
                title: "CI/CD Evidence",
                desc: "Link build and test results directly to the change request for end-to-end traceability."
            },
            {
                path: "/work-item-linking",
                title: "Work Item Linking",
                desc: "Guidance on linking approved work items to code changes for audit evidence."
            },
            {
                path: "/exception-tracking",
                title: "Exception Tracking",
                desc: "Document emergency fixes and policy bypasses with formal exception records."
            },
            {
                path: "/features",
                title: "Manual Evidence Upload",
                desc: "Upload files, screenshots, and documents as compliance evidence. Each item is SHA-256 checksum-verified, linked to a control and framework, and moves through a submit \u2192 review \u2192 approve workflow with a complete audit trail."
            }
        ]
    }
];

export default function SolutionsHubPage() {
    return (
        <MarketingLayout
            title="Compliance Solutions & Audit Evidence | EvidentTrail"
            description="Browse solutions for continuous audit evidence, GitHub change control, and compliance frameworks. Learn how EvidentTrail exports tamper-evident evidence packs."
            h1="Compliance Solutions"
            heroText="Explore how EvidentTrail maps GitHub change control evidence to audit, security, and engineering needs."
        >
            <BreadcrumbJsonLd
                items={[
                    { name: 'Home', url: 'https://evidenttrail.com/' },
                    { name: 'Solutions', url: 'https://evidenttrail.com/solutions' }
                ]}
            />
            <div className={tokens.container}>
                <div className="py-12 space-y-16">
                    {solutions.map((section) => (
                        <section key={section.category}>
                            <h2 className={`${tokens.heading.h2} mb-8 border-b border-slate-800 pb-4`}>
                                {section.category}
                            </h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {section.items.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`${tokens.card} block hover:border-primary-500/50 transition-colors group h-full`}
                                    >
                                        <h3 className={`${tokens.heading.h3} mb-3 group-hover:text-primary-400 transition-colors`}>
                                            {item.title}
                                        </h3>
                                        <p className={tokens.text.base}>
                                            {item.desc}
                                        </p>
                                        <div className="mt-4 text-primary-400 text-sm font-medium flex items-center gap-1">
                                            Read more
                                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </MarketingLayout>
    );
}
